import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import useConfigStore from '../../stores/configStore';
import useMapStateStore from '../../stores/mapStateStore';
import { useAppStore } from '../../stores';
import useWindowStore from '../../stores/windowStore';
import useAnchoredModal from '../Modal/useAnchoredModal';
import useTourGuideState from './hooks/useTourGuideState';
import useScrollManagement from './hooks/useScrollManagement';
import useIdleTimeout from './hooks/useIdleTimeout';
import useSessionManagement from './hooks/useSessionManagement';
import useConversationInit from './hooks/useConversationInit';
import useFormSubmit from './hooks/useFormSubmit';
import useAdditionalInterests from './hooks/useAdditionalInterests';
import useButtonClick from './hooks/useButtonClick';

import { loadSavedLeadInfo } from './utils/storage';
import { buildMorePlacesMessage, buildNextStepMessage } from './utils/conversationFlow';
import { logBotMessage } from './utils/api';

import SlidingPanel from '../SlidingPanel/SlidingPanel';
import CTA from './components/CTA';
import MessageList from './components/MessageList';
import InputBar from './components/InputBar';

import s from './TourGuideAi.module.scss';

const TourGuideAi = ({
  onAmenitySelected, onExternalLocationFound, onResetToCommunity, onEnsureZoomZoneZoom,
}) => {
  const isOpen = useMapStateStore((s) => s.tgaiPanelOpen);
  const setIsOpen = useMapStateStore((s) => s.setTgaiPanelOpen);
  const { state, dispatch } = useTourGuideState();

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, [setIsOpen]);

  const config = useConfigStore((storeState) => storeState.config);
  const settings = useAppStore((storeState) => storeState.settings);
  const isMobile = useWindowStore((storeState) => storeState.isMobile);

  const { anchorRef, modalStyle, arrowStyle } = useAnchoredModal({
    align: ['right', 'centerY'],
    padding: 45,
    isMobile,
  });

  const scrollManagement = useScrollManagement({
    isOpen, messages: state.messages, isAiThinking: state.isAiThinking,
  });
  const { messagesEndRef, setScrollPosition, saveScrollPosition, hasContentBelow, hasContentAbove, scrollToBottom, unreadCount } = scrollManagement;

  const { initializeConversation } = useConversationInit({
    state, dispatch, isOpen, setScrollPosition, config,
  });

  const { handleSessionEnd, handleRestartSession } = useSessionManagement({
    state, dispatch, setScrollPosition, onAmenitySelected, config,
  });

  const { handleFormSubmit } = useFormSubmit({ state, dispatch, communityNid: config?.communityNid, config });

  const { recordActivity, scheduleDeferredMessage, cancelDeferredMessage, flushDeferredMessage, handleIdleTimeoutYes, handleIdleTimeoutNo } = useIdleTimeout({
    isOpen, state, dispatch, onSessionEnd: handleSessionEnd, communityNid: config?.communityNid, communityName: config?.communityName,
  });

  const { handleAdditionalInterestsSubmit } = useAdditionalInterests({
    state,
    dispatch,
    communityNid: config?.communityNid,
    communityName: config?.communityName,
    recordActivity,
    scheduleDeferredMessage,
    onExternalLocationFound,
    onAmenitySelected,
    onResetToCommunity,
    onEnsureZoomZoneZoom,
  });

  const lastBotMessage = [...state.messages].reverse().find((msg) => msg.type === 'bot');
  const lastBotHasButtons = lastBotMessage?.buttons?.length > 0;
  const lastBotIsNextStepPrompt = lastBotMessage?.stepType === 'next_step_prompt'
    || lastBotMessage?.stepType === 'more_places_prompt';

  const showPersistentInput = !state.isSessionEnded && !state.isAiThinking && (
    lastBotIsNextStepPrompt
    || (!lastBotHasButtons && (
      state.conversationState.currentStep === 'step4_followup'
      || state.conversationState.currentStep === 'share_interest'
      || state.conversationState.currentStep === 'step_ask_question'
      || state.hasShownFollowUp
      || state.isAiMode
    ))
  );

  const handleSideNavToggle = () => {
    handleToggle();
  };

  const { handleButtonClick } = useButtonClick({
    state,
    dispatch,
    config,
    handlers: {
      recordActivity,
      scheduleDeferredMessage,
      cancelDeferredMessage,
      flushDeferredMessage,
      handleSessionEnd,
      handleRestartSession,
      handleIdleTimeoutYes,
      handleIdleTimeoutNo,
      saveScrollPosition,
      scrollToBottom,
      handleSideNavToggle,
      initializeConversation,
      onAmenitySelected,
      onExternalLocationFound,
      onResetToCommunity,
      onEnsureZoomZoneZoom,
    },
  });

  const focusAnimationComplete = useMapStateStore((storeState) => storeState.focusAnimationComplete);
  const amenityModalOpen = useMapStateStore((storeState) => storeState.amenityModalOpen);
  const { pendingExternalVisit } = state.conversationState;

  // Track modal open→close transitions so we only advance after the user
  // actively closes a modal (not on the initial false state).
  const prevAmenityModalOpenRef = useRef(amenityModalOpen);
  useEffect(() => {
    const wasOpen = prevAmenityModalOpenRef.current;
    prevAmenityModalOpenRef.current = amenityModalOpen;
    if (!(wasOpen && !amenityModalOpen)) return;
    if (!state.conversationState.pendingAllVisitedAdvance) return;

    const firstName = state.conversationState.userInfo?.firstName || '';
    dispatch({
      type: 'REPLACE_CONVERSATION_STATE',
      payload: { ...state.conversationState, pendingAllVisitedAdvance: false },
    });
    const logCtx = {
      sessionId: state.sessionId,
      selectedInterests: state.conversationState.selectedInterests?.map((i) => i.uuid) || [],
      userInfo: state.conversationState.userInfo,
      communityNid: config?.communityNid,
    };
    if (state.pendingPlaces && state.pendingPlaces.length > 0) {
      const moreMsg = buildMorePlacesMessage(state.pendingPlacesInterests, firstName, config);
      dispatch({ type: 'APPEND_MESSAGES', payload: [moreMsg] });
      if (moreMsg?.text) {
        logBotMessage(moreMsg.text, { ...logCtx, currentStep: 'more_places', questionType: 'more_places_prompt' });
      }
    } else {
      const nextStep = buildNextStepMessage(firstName, config);
      if (nextStep) {
        dispatch({ type: 'APPEND_MESSAGES', payload: [nextStep] });
        dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
        logBotMessage(nextStep.text, { ...logCtx, currentStep: 'next_step', questionType: 'next_step_prompt' });
      }
    }
  }, [amenityModalOpen]);

  useEffect(() => {
    if (focusAnimationComplete && pendingExternalVisit) {
      const alreadyVisited = state.conversationState.visitedExternalLocations?.includes(pendingExternalVisit);
      dispatch({
        type: 'REPLACE_CONVERSATION_STATE',
        payload: {
          ...state.conversationState,
          visitedExternalLocations: alreadyVisited
            ? state.conversationState.visitedExternalLocations
            : [...(state.conversationState.visitedExternalLocations || []), pendingExternalVisit],
          pendingExternalVisit: null,
        },
      });
    }
  }, [focusAnimationComplete, pendingExternalVisit]);

  const { enableTourGuideAI } = config || {};
  const { platform } = settings || {};
  const showTourGuideAI = enableTourGuideAI && platform === 'siteSee' && !isMobile;

  // Test hatch: when ?tgai_debug=1 is in the URL, expose setIsOpen and
  // dispatch on window so Playwright specs can open the panel and
  // inject messages without walking the full conversation flow. Only
  // installs when TGAI would actually render — otherwise the hatch
  // would falsely signal "TGAI is mounted" on mobile or when the
  // feature is disabled.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!showTourGuideAI) return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tgai_debug') !== '1') return undefined;
    window.__tgaiSetOpen = setIsOpen;
    window.__tgaiDispatch = dispatch;
    return () => {
      delete window.__tgaiSetOpen;
      delete window.__tgaiDispatch;
    };
  }, [dispatch, showTourGuideAI]);

  if (!showTourGuideAI) return null;

  return (
    <>
      <CTA
        panelIsOpen={isOpen}
        onStartTour={handleToggle}
        modalStyle={modalStyle}
        arrowStyle={arrowStyle}
        communityName={config?.communityName}
      />

      <SlidingPanel
        side="right"
        isOpen={isOpen}
        onToggle={handleToggle}
        toggleRef={anchorRef}
        className={s.panel}
      >
        <div className={s.tourGuideAi} data-testid="tgai-panel">
          <MessageList
            messages={state.messages}
            formState={{
              isFormSubmitted: state.isFormSubmitted,
              savedLeadInfo: loadSavedLeadInfo(),
            }}
            conversationState={state.conversationState}
            isAiThinking={state.isAiThinking}
            aiThinkingWithLabel={state.aiThinkingWithLabel}
            messagesEndRef={messagesEndRef}
            hasContentBelow={hasContentBelow}
            hasContentAbove={hasContentAbove}
            onScrollToBottom={scrollToBottom}
            unreadCount={unreadCount}
            handlers={{
              onFormSubmit: handleFormSubmit,
              onButtonClick: handleButtonClick,
            }}
          />
          <InputBar
            value={state.additionalInterests}
            onChange={(val) => dispatch({ type: 'SET_ADDITIONAL_INTERESTS', payload: val })}
            onSubmit={() => handleAdditionalInterestsSubmit()}
            onActivity={recordActivity}
            disabled={!showPersistentInput}
          />
        </div>
      </SlidingPanel>
    </>
  );
};

TourGuideAi.propTypes = {
  onAmenitySelected: PropTypes.func,
  onExternalLocationFound: PropTypes.func,
  onResetToCommunity: PropTypes.func,
  onEnsureZoomZoneZoom: PropTypes.func,
};

TourGuideAi.defaultProps = {
  onAmenitySelected: null,
  onExternalLocationFound: null,
  onResetToCommunity: null,
  onEnsureZoomZoneZoom: null,
};

export default TourGuideAi;
